/**
 * How the investigator got there: the chain from trigger to failure, what it saw in the
 * trace, and the calls it made. A separate panel so "why believe you" does not bury "what broke".
 */

import { ExternalLink } from 'lucide-react';
import { formatDuration } from '@/lib/duration';
import type { DecisionItem, EvidenceItem, VerdictReport } from '../report-data';
import { ReportSection, Stagger } from './report-bits';

/** The chain as a climbing rail: each step caused the one under it. */
const CauseChain = ({ steps }: { steps: string[] }) => (
  <ol className='flex flex-col'>
    {steps.map((step, index) => (
      <li key={index} className='flex gap-3'>
        <div className='flex flex-col items-center'>
          <span className='flex size-5 shrink-0 items-center justify-center rounded-full border border-indigo-200 bg-indigo-50 text-[10px] font-medium text-indigo-700'>
            {index + 1}
          </span>
          {index < steps.length - 1 && <span className='my-1 w-px flex-1 bg-gray-500' />}
        </div>
        <p className='flex-1 pt-0.5 pb-3 text-[13px] leading-5 text-text-body'>{step}</p>
      </li>
    ))}
  </ol>
);

const Observations = ({ items }: { items: EvidenceItem[] }) => (
  <ul className='flex flex-col gap-1.5'>
    {items.map((item, index) => (
      <li key={index} className='flex flex-col gap-0.5 border-l border-gray-500 pl-3'>
        {item.ref && <span className='font-mono text-[10px] text-gray-800'>{item.ref}</span>}
        <p className='text-xs leading-4.5 text-text-body'>{item.observation}</p>
      </li>
    ))}
  </ul>
);

const Decisions = ({ items }: { items: DecisionItem[] }) => (
  <ul className='flex flex-col gap-2'>
    {items.map((item, index) => (
      <li key={index} className='flex flex-col gap-0.5'>
        <p className='text-[13px] leading-5 font-medium text-text-heading'>{item.decision}</p>
        {item.reasoning && <p className='text-xs leading-4.5 text-gray-800'>{item.reasoning}</p>}
      </li>
    ))}
  </ul>
);

/** What the run itself cost, and the way back to the trace it was read from. */
const RunMeta = ({ meta }: { meta: VerdictReport['meta'] }) => {
  const facts: string[] = [];
  if (meta.model) facts.push(meta.model);
  if (meta.iterations !== null) facts.push(`${meta.iterations} iterations`);
  if (meta.wallSeconds !== null) facts.push(formatDuration(meta.wallSeconds * 1000));
  if (meta.costUsd !== null) facts.push(`$${meta.costUsd.toFixed(3)}`);
  if (!facts.length && !meta.traceUrl) return null;

  return (
    <div className='flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-500 pt-3'>
      <span className='text-[11px] text-gray-800 tabular-nums'>{facts.join(' · ')}</span>
      {meta.traceUrl && (
        <a
          href={meta.traceUrl}
          target='_blank'
          rel='noreferrer'
          className='inline-flex items-center gap-1 text-[11px] text-indigo-700 transition-colors hover:text-indigo-900'
        >
          Investigator trace
          <ExternalLink className='size-3' aria-hidden />
        </a>
      )}
    </div>
  );
};

export const EvidencePanel = ({ report }: { report: VerdictReport }) => (
  <Stagger className='gap-6'>
    {[
      report.causeChain.length > 0 ? (
        <ReportSection key='chain' title='How it got there'>
          <CauseChain steps={report.causeChain} />
        </ReportSection>
      ) : null,

      report.evidence.length > 0 ? (
        <ReportSection
          key='evidence'
          title='What the trace showed'
          aside={<span className='text-[11px] text-gray-800'>{report.evidence.length}</span>}
        >
          <Observations items={report.evidence} />
        </ReportSection>
      ) : null,

      report.decisions.length > 0 ? (
        <ReportSection key='decisions' title='Calls the investigator made'>
          <Decisions items={report.decisions} />
        </ReportSection>
      ) : null,

      <RunMeta key='meta' meta={report.meta} />,
    ]}
  </Stagger>
);
